import React, {useEffect, useReducer, useState, useCallback} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import throttle from 'lodash.throttle'
import ms from 'ms'
import detectPassiveEventsSupport from '../../utils/detectPassiveEventsSupport'
import {ScreenSizeContext} from './context'

const getScreenHeight = (): number | undefined =>
  (typeof window !== 'undefined' && window?.innerHeight) ||
  (typeof document !== 'undefined' && document?.documentElement?.clientHeight) ||
  (typeof document !== 'undefined' && document?.getElementsByTagName('body')[0]?.clientHeight) ||
  undefined

const getScreenWidth = (): number | undefined =>
  (typeof window !== 'undefined' && window?.innerWidth) ||
  (typeof document !== 'undefined' && document?.documentElement?.clientWidth) ||
  (typeof document !== 'undefined' && document?.getElementsByTagName('body')[0]?.clientWidth) ||
  undefined

const propTypes = {
  children: PropTypes.node.isRequired,
}
const defaultProps = {}

export type ScreenSizeProviderProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const ScreenSizeProvider: React.FC<ScreenSizeProviderProps> = ({children}) => {
  const [screenHeight, setScreenHeight] = useState(getScreenHeight())
  const [screenWidth, setScreenWidth] = useState(getScreenWidth())
  const [screenSizeUpdate, increaseTick] = useReducer(x => x + 1, 0)

  const handleResize = useCallback(() => {
    setScreenHeight(getScreenHeight())
    setScreenWidth(getScreenWidth())
    increaseTick()
  }, [])

  useEffect(() => {
    const handler = throttle(handleResize, ms('100ms'))

    if (detectPassiveEventsSupport()) {
      window.addEventListener('resize', handler, {passive: true})
    } else {
      window.addEventListener('resize', handler)
    }

    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [handleResize])

  return (
    <ScreenSizeContext.Provider
      value={{
        screenWidth,
        screenHeight,
        screenSizeUpdate,
      }}
    >
      {children}
    </ScreenSizeContext.Provider>
  )
}

ScreenSizeProvider.propTypes = propTypes
ScreenSizeProvider.defaultProps = defaultProps

export default React.memo(ScreenSizeProvider)
